import { Injectable } from '@angular/core';
import { CanActivate, ActivatedRouteSnapshot, RouterStateSnapshot, Router, CanActivateChild } from '@angular/router';
import { JwtHelperService } from '@auth0/angular-jwt';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { AuthService } from '@services/auth.service';
import { UiService } from '@services/ui.service';
import { UserService } from '@services/user.service';

@Injectable({
    providedIn: 'root'
})
export class AuthGuard implements CanActivate, CanActivateChild {
    constructor(
        private authSrv: AuthService,
        private uiSrv: UiService,
        private userSrv: UserService,
        private router: Router,
        private jwtHelperSrv: JwtHelperService
    ) { }

    canActivate(
        next: ActivatedRouteSnapshot,
        state: RouterStateSnapshot): Observable<boolean> | Promise<boolean> | boolean {
        // No token
        if (!this.jwtHelperSrv.tokenGetter()) {
            this.authSrv.redirectUrl = state.url;
            this.authSrv.logout();
            return false;
        }

        // User password not set
        if (this.needResetPwd()) {
            this.uiSrv.showSnackbar('auth.updPwdReq.summary', true, 5000);
            this.authSrv.authChanged.next(false);

            this.router.navigate(['/change-password']);
            return false;
        }

        // User role cannot access this route
        if (!this.authSrv.isAuthorized(next.data.roles, next.data.providers)) {
            this.router.navigate(['/']);
            return false;
        }

        // token valid and up to date
        if (this.authSrv.isAuth()) {
            this.userSrv.cleanSelectedSimulation();
            return true;
        }

        // Token exists but outdated
        return this.refreshToken();
    }

    canActivateChild(
        route: ActivatedRouteSnapshot,
        state: RouterStateSnapshot): Observable<boolean> | Promise<boolean> | boolean  {
        return this.canActivate(route, state);
    }

    private needResetPwd(): boolean {
        const decodedToken = this.authSrv.getDecodedToken();

        if (decodedToken && decodedToken.upd_pwd_req) {
            return true;
        }

        return false;
    }

    private refreshToken(): Observable<boolean> {
        this.uiSrv.routeLoadingStateChanged.next(true);

        return this.authSrv.refreshToken().pipe(
            map(() => {
                this.uiSrv.routeLoadingStateChanged.next(false);
                return true;
            }),
            catchError(error => {
                this.uiSrv.routeLoadingStateChanged.next(false);
                this.authSrv.logout();
                return of(error);
            })
        );
    }
}
